import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { currentIdToken, subscribe } from "./session";
import { signOut } from "./signOut";

/**
 * Auth state for the UI. Signing in happens on the app's own /login, /signup,
 * and /forgot-password routes, which write tokens through `lib/session`; this
 * context reflects what is stored and offers the way out.
 */
interface AuthState {
  idToken: string | null;
  email: string | null;
  /** Kept for call sites that gate on it; there is no async startup work now. */
  ready: boolean;
  /** Ends the session and returns to the public home page. */
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function decodeEmail(token: string): string | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const payload = JSON.parse(atob(part)) as { email?: string };
    return payload.email ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(() => currentIdToken());
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Sign-in, sign-out, and silent renewal all run outside React; this keeps the
  // header and the route gates in step with whatever the session holds.
  useEffect(() => subscribe(() => setIdToken(currentIdToken())), []);

  return (
    <AuthContext.Provider
      value={{
        idToken,
        email: idToken ? decodeEmail(idToken) : null,
        ready: true,
        // Every way out goes through one place, so no call site can clear the
        // tokens and leave the visitor sitting on an account page.
        signOut: () => signOut(queryClient, navigate),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
